import {VueController} from '@/service/support/vuex-controller';
import vaultService from '@/service/vault-service';
import WebViewer from '@pdftron/webviewer';

export default VueController("SendForSignature", {
    props: {
        portals: {
            type: Array,
            default: () => []
        },
        organization: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        preselectedPortal: {
            type: String,
            default: null
        },
        preselectedFile: {
            type: Object,
            default: null
        },
        signatureRequestsByFile: {
            type: Object,
            default: () => ({})
        },
        bulkMode: {
            type: Boolean,
            default: false
        },
        selectedPortalIds: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            step: 1,
            pdfSourceTab: 0,
            isDraggingPdf: false,
            selectedFile: null,
            selectedExistingFile: null,
            existingPdfFiles: [],
            loadingFiles: false,
            selectedPortal: null,
            createNewPortal: false,
            newPortalName: '',
            recipients: [],
            customMessage: '',
            signatureFields: [],
            fieldAssignments: {}, // { signatureFieldId: recipientIndex }
            fieldOrder: [],
            pdfViewerLoaded: false,
            pdfViewerInstance: null,
            selectedFieldId: null,
            sending: false,
            sendingProgress: 0,
            currentSendingPortal: '',
            errorMessage: null,
            successMessage: null,
            emailRules: [
                v => !!v || 'Signer email is required',
                v => /.+@.+\..+/.test(v) || 'Must be a valid email address'
            ],
            recipientColors: ['#1976D2', '#388E3C', '#7B1FA2', '#F57C00', '#C2185B', '#00796B', '#5D4037', '#455A64'],
            orgMembers: []  // All organization members and admins
        };
    },
    computed: {
        hasSelectedPdf() {
            return this.selectedFile !== null || this.selectedExistingFile !== null;
        },
        portalOptions() {
            if (!this.portals) return [];
            return this.portals.map(portal => ({
                id: portal.id,
                name: portal.name
            }));
        },
        selectedPortalsData() {
            if (!this.bulkMode || !this.selectedPortalIds || this.selectedPortalIds.length === 0) {
                return [];
            }
            return this.portals.filter(portal => this.selectedPortalIds.indexOf(portal.id) > -1);
        },
        portalGuestEmails() {
            if (!this.portals || !this.selectedPortal) return [];
            const portal = this.portals.find(p => p.id === this.selectedPortal);
            if (!portal || !portal.permissions) return [];
            // Include all active users (guests, members, admins)
            return portal.permissions
                .filter(perm => perm.user_email && perm._deleted !== 1)
                .map(perm => perm.user_email);
        },
        portalGuests() {
            const allGuests = [];
            
            // Add all portal users (guests, members, admins) who can receive signature request emails
            if (this.portals && this.selectedPortal) {
                const portal = this.portals.find(p => p.id === this.selectedPortal);
                if (portal && portal.permissions) {
                    console.log('Portal permissions:', portal.permissions.length, 'total permissions');
                    console.log('Portal org_id:', portal.org_id);
                    console.log('Current organization:', this.organization && this.organization.id);
                    
                    const guests = portal.permissions
                        .filter(perm => {
                            // Include all active users with emails (guests, members, admins)
                            const isValid = perm.user_email && 
                                   perm._deleted !== 1 && 
                                   (perm.user_type === 'guest' || perm.user_type === 'member' || perm.user_type === 'admin');
                            
                            if (perm.user_email) {
                                console.log(`  - ${perm.user_email} (${perm.user_type}, deleted: ${perm._deleted}): ${isValid ? 'INCLUDED' : 'excluded'}`);
                            }
                            
                            return isValid;
                        })
                        .map(perm => {
                            let displayType = perm.user_type;
                            
                            // Security check: Only show as "admin" if they're actually the admin of THIS portal's organization
                            if (perm.user_type === 'admin') {
                                // Check if this portal belongs to the current user's organization
                                const isOwnOrgPortal = this.organization && portal.org_id === this.organization.id;
                                
                                if (isOwnOrgPortal) {
                                    // This is our org's portal - verify they're our org's admin
                                    const isVerifiedAdmin = this.organization.administrator_user_id === perm.user_id;
                                    if (!isVerifiedAdmin) {
                                        displayType = 'guest'; // They're marked as admin but not the admin of this org
                                        console.log(`    ⚠️ ${perm.user_email} has admin type but is not the admin of org ${this.organization.id}, showing as guest`);
                                    }
                                } else {
                                    // Different org's portal or no org context - treat as guest
                                    displayType = 'guest';
                                    console.log(`    ⚠️ ${perm.user_email} is admin elsewhere, showing as guest in this portal`);
                                }
                            }
                            
                            return {
                                email: perm.user_email,
                                name: perm.user_name || perm.user_email,
                                type: displayType,
                                originalType: perm.user_type,
                                isSelf: this.user && perm.user_id === this.user.id
                            };
                        });
                    allGuests.push(...guests);
                }
            }
            
            console.log('portalGuests computed:', allGuests.length, 'recipients available (guests + members + admins)', allGuests);
            return allGuests;
        },
        fieldAssignmentOptions() {
            // Options for assigning signature fields (includes recipients + organization + not required + any guest)
            const options = [];
            
            // Add "Not Required" as first option
            options.push({
                text: 'Not Required',
                value: 'not_required',
                color: '#9E9E9E', // Grey color for optional fields
                isNotRequired: true,
                recipient: null
            });
            
            // Add "Any Guest" as second option
            options.push({
                text: 'Any Guest',
                value: 'any_guest',
                color: '#00ACC1', // Cyan/teal color for any guest
                isAnyGuest: true,
                recipient: {
                    name: 'Any Guest',
                    email: 'any_guest',
                    isAnyGuest: true,
                    color: '#00ACC1'
                }
            });
            
            // Add each recipient as an option (only in single portal mode)
            if (!this.bulkMode) {
                this.recipients.forEach((recipient, index) => {
                    options.push({
                        text: recipient.name || recipient.email,
                        value: index,
                        color: recipient.color,
                        isOrganization: false,
                        recipient: recipient
                    });
                });
            }
            
            // Add the ORGANIZATION as a field assignment option (not an email recipient)
            if (this.organization && this.organization.companyName) {
                options.push({
                    text: this.organization.companyName,
                    value: 'org',
                    color: '#5764a9', // Organization color
                    isOrganization: true,
                    recipient: {
                        name: this.organization.companyName,
                        email: `org:${this.organization.id}`,
                        isOrganization: true,
                        color: '#5764a9'
                    }
                });
                console.log('Added organization to field assignment options:', this.organization.companyName);
            }
            
            console.log('fieldAssignmentOptions:', options.length, 'options available');
            return options;
        },
        isStep2Valid() {
            // In bulk mode, we only need selected portals (no individual recipients)
            if (this.bulkMode) {
                return this.selectedPortalIds && this.selectedPortalIds.length > 0;
            }
            
            // In single mode, we need both portal and recipients
            const hasPortal = this.selectedPortal || (this.createNewPortal && this.newPortalName);
            const hasRecipients = this.recipients.length > 0 && this.recipients.every(r => {
                // Recipients must have valid email addresses
                return r.email && /.+@.+\..+/.test(r.email);
            });
            return hasPortal && hasRecipients;
        },
        canSendSignatureRequest() {
            // Check if we can send the signature request
            if (this.sending || !this.pdfViewerLoaded) {
                return false;
            }
            
            if (this.bulkMode) {
                // In bulk mode, we just need field assignments (any_guest, org, or not_required)
                // No individual recipients needed
                return true;
            } else {
                // In single mode, we need valid recipients
                return this.recipients.length > 0 && this.recipients.every(r => r.email && /.+@.+\..+/.test(r.email));
            }
        },
        // All signature fields with their assignments
        assignedSignatureFields() {
            return this.signatureFields.map(field => {
                const assignment = this.fieldAssignments[field.id];
                let recipientColor = null;
                
                if (assignment === 'not_required') {
                    recipientColor = '#9E9E9E'; // Grey for optional fields
                } else if (assignment === 'any_guest') {
                    recipientColor = '#00ACC1'; // Cyan/teal for any guest
                } else if (assignment === 'org') {
                    recipientColor = '#5764a9'; // Organization color
                } else if (assignment !== undefined) {
                    recipientColor = this.recipientColors[assignment];
                }
                
                return {
                    ...field,
                    assignedTo: assignment,
                    recipientColor: recipientColor
                };
            });
        }
    },
    mounted() {
        this.loadExistingPdfFiles();
        this.loadOrgMembers();
        
        // Debug: Log organization info
        console.log('SendForSignature - Organization:', this.organization);
        if (this.organization && this.organization.companyName) {
            console.log('Organization is available as a recipient:', this.organization.companyName);
        } else {
            console.log('Organization not available - missing companyName');
        }
        
        // If portal is preselected, set it
        if (this.preselectedPortal) {
            this.selectedPortal = this.preselectedPortal;
        }
        
        // If file is preselected, set it (only in single portal mode)
        if (this.preselectedFile && !this.bulkMode) {
            if (this.preselectedFile.id) {
                this.selectedExistingFile = this.preselectedFile.id;
                this.pdfSourceTab = 1;
            }
        }
        
        // Force upload tab in bulk mode
        if (this.bulkMode) {
            this.pdfSourceTab = 0;
            console.log('Bulk mode enabled - forcing upload new PDF tab');
        }
        
        // Add a blank recipient by default
        this.addRecipient();
    },
    watch: {
        bulkMode(newVal) {
            // Force upload tab when entering bulk mode
            if (newVal) {
                this.pdfSourceTab = 0;
                console.log('Switched to bulk mode - forcing upload new PDF tab');
            }
        },
        step(newStep) {
            if (newStep === 3 && !this.pdfViewerLoaded) {
                this.loadPdfViewer();
            }
        },
        recipients: {
            handler() {
                console.log('Recipients changed:', this.recipients.map(r => ({ 
                    name: r.name, 
                    email: r.email, 
                    isOrganization: r.isOrganization 
                })));
                // Re-run auto-assignment when recipients change (only in single mode)
                if (!this.bulkMode && this.signatureFields.length > 0) {
                    this.autoAssignFieldsToRecipients();
                }
            },
            deep: true
        },
        selectedPortal(newPortal, oldPortal) {
            // When portal changes, reset isExistingGuest flags and re-validate (only in single mode)
            if (!this.bulkMode && newPortal !== oldPortal) {
                this.recipients.forEach((recipient, index) => {
                    // Check if email still exists in new portal
                    const existingGuest = this.portalGuests.find(
                        guest => guest.email && guest.email.toLowerCase() === (recipient.email || '').toLowerCase()
                    );
                    
                    if (existingGuest) {
                        this.$set(this.recipients[index], 'name', existingGuest.name);
                        this.$set(this.recipients[index], 'isExistingGuest', true);
                    } else {
                        this.$set(this.recipients[index], 'isExistingGuest', false);
                    }
                });
            }
        }
    },
    methods: {
        closeDialog() {
            // Prevent closing while sending in bulk mode
            if (this.sending && this.bulkMode) {
                return;
            }
            this.$emit('close');
        },
        addRecipient() {
            const colorIndex = this.recipients.length % this.recipientColors.length;
            this.recipients.push({
                name: '',
                email: '',
                color: this.recipientColors[colorIndex],
                order: this.recipients.length,
                isExistingGuest: false
            });
        },
        removeRecipient(index) {
            this.recipients.splice(index, 1);
            // Re-assign colors and orders
            this.recipients.forEach((recipient, idx) => {
                recipient.order = idx;
                recipient.color = this.recipientColors[idx % this.recipientColors.length];
            });
            // Re-run auto-assignment if signature fields are already detected (only in single mode)
            if (!this.bulkMode && this.signatureFields.length > 0) {
                this.autoAssignFieldsToRecipients();
            }
        },
        onRecipientEmailChange(index, value) {
            // Handle both object (from selecting) and string (from typing)
            let newEmail = '';
            let recipientData = null;
            
            if (value && typeof value === 'object' && value.email) {
                // User selected from dropdown - it's an object
                newEmail = value.email;
                recipientData = value;
            } else if (typeof value === 'string') {
                // User typed an email
                newEmail = value;
            }
            
            console.log('onRecipientEmailChange:', { index, value, newEmail, recipientData });
            
            // Check if the email matches an existing guest
            const existingGuest = this.portalGuests.find(
                guest => guest.email && guest.email.toLowerCase() === (newEmail || '').toLowerCase()
            );
            
            if (existingGuest) {
                // Auto-fill name and mark as existing guest
                console.log('Setting recipient as existing guest:', existingGuest.name);
                this.$set(this.recipients[index], 'email', existingGuest.email);
                this.$set(this.recipients[index], 'name', existingGuest.name);
                this.$set(this.recipients[index], 'isExistingGuest', true);
            } else {
                // New email - allow editing
                console.log('Setting recipient as new email:', newEmail);
                this.$set(this.recipients[index], 'email', newEmail);
                this.$set(this.recipients[index], 'isExistingGuest', false);
            }
        },
        getRecipientIcon(item) {
            if (!item) return 'mdi-account';
            if (item.isAnyGuest) return 'mdi-account-multiple';
            if (item.isOrganization) return 'mdi-domain';
            if (item.type === 'admin') return 'mdi-shield-account';
            if (item.type === 'member') return 'mdi-account-tie';
            if (item.type === 'guest') return 'mdi-account-check';
            if (item.isSelf) return 'mdi-account-circle';
            return 'mdi-account';
        },
        getRecipientIconColor(item) {
            if (!item) return 'grey';
            if (item.isAnyGuest) return 'cyan';
            if (item.isOrganization) return 'primary';
            if (item.type === 'admin') return 'error';
            if (item.type === 'member') return 'primary';
            if (item.type === 'guest') return 'success';
            return 'primary';
        },
        getRecipientChipColor(item) {
            if (!item) return 'grey';
            if (item.isAnyGuest) return 'cyan';
            if (item.isOrganization) return 'primary';
            if (item.type === 'admin') return 'error';
            if (item.type === 'member') return 'primary';
            if (item.type === 'guest') return 'success';
            return 'info';
        },
        getRecipientTypeLabel(item) {
            if (!item) return '';
            if (item.isAnyGuest) return 'Any Guest';
            if (item.isOrganization) return 'Organization';
            if (item.type === 'admin') return 'Admin';
            if (item.type === 'member') return 'Member';
            if (item.type === 'guest') return 'Guest';
            return item.type || 'guest';
        },
        getRecipientDisplayName(item) {
            if (!item) return '';
            if (typeof item === 'string') return item;
            if (item.isOrganization) return item.name;
            if (item.name && item.name !== item.email) return item.name;
            return item.email || '';
        },
        onPdfDragEnter(event) {
            event.preventDefault();
            event.stopPropagation();
            // Only enable drag state if on Step 1 and Upload tab (tab 0)
            if (this.step === 1 && this.pdfSourceTab === 0) {
                this.isDraggingPdf = true;
                console.log('📥 PDF drag enter (Step 1, Upload tab)');
            }
        },
        onPdfDragOver(event) {
            event.preventDefault();
            event.stopPropagation();
            // console.log('📥 PDF drag over');
        },
        onPdfDragLeave(event) {
            event.preventDefault();
            event.stopPropagation();
            // Only set dragging to false if we're leaving the card entirely
            // Check if the related target is outside the drop zone
            const dropZone = this.$refs.pdfDropZone && this.$refs.pdfDropZone.$el;
            if (dropZone && event.relatedTarget && !dropZone.contains(event.relatedTarget)) {
                this.isDraggingPdf = false;
                console.log('📥 PDF drag leave (outside drop zone)');
            }
        },
        onPdfDrop(event) {
            event.preventDefault();
            event.stopPropagation();
            this.isDraggingPdf = false;
            
            console.log('💧💧💧 PDF DROP event triggered!');
            console.log('Current step:', this.step, 'Current tab:', this.pdfSourceTab);
            
            // Only handle drop if on Step 1 and Upload tab (tab 0)
            if (this.step !== 1 || this.pdfSourceTab !== 0) {
                console.log('❌ Not on correct step/tab for PDF upload');
                return;
            }
            
            const files = event.dataTransfer.files;
            console.log('📦 Files detected in drop:', files ? files.length : 0, files);
            
            if (files && files.length > 0) {
                const file = files[0];
                console.log('📄 File type:', file.type, 'Name:', file.name);
                if (file.type === 'application/pdf') {
                    this.selectedFile = file;
                    console.log('✅ PDF file selected:', file.name);
                } else {
                    this.errorMessage = 'Please select a PDF file';
                    console.log('❌ Invalid file type:', file.type);
                }
            } else {
                console.log('❌ No files dropped');
            }
        },
        handlePdfFileSelect(event) {
            event.stopPropagation();
            event.preventDefault();
            const files = event.target.files;
            
            console.log('📁 PDF file input triggered, files:', files ? files.length : 0);
            
            if (files && files.length > 0) {
                const file = files[0];
                console.log('📄 File type:', file.type, 'Name:', file.name);
                if (file.type === 'application/pdf') {
                    this.selectedFile = file;
                    console.log('✅ PDF file selected:', file.name);
                } else {
                    this.errorMessage = 'Please select a PDF file';
                    console.log('❌ Invalid file type:', file.type);
                }
            }
        },
        clearSelectedFile() {
            this.selectedFile = null;
            if (this.$refs.pdfFileInput) {
                this.$refs.pdfFileInput.value = '';
            }
        },
        async loadOrgMembers() {
            // Load all organization members and admins
            try {
                if (!this.organization || !this.organization.id) {
                    console.log('No organization found, skipping member load');
                    return;
                }
                
                const members = await vaultService.query('getMembersForOrgId', {
                    org_id: this.organization.id
                }, undefined, {vuexCache: false});
                
                if (members && Array.isArray(members)) {
                    this.orgMembers = members.filter(m => m.email && m.status !== 'disabled').map(m => ({
                        id: m.id,
                        email: m.email,
                        displayName: m.displayName || m.email,
                        userType: m.administratorOf === this.organization.id ? 'admin' : 'member'
                    }));
                    console.log(`Loaded ${this.orgMembers.length} organization members`);
                } else {
                    this.orgMembers = [];
                }
            } catch (error) {
                console.error('Error loading organization members:', error);
                this.orgMembers = [];
            }
        },
        async loadExistingPdfFiles() {
            this.loadingFiles = true;
            try {
                // Load PDF files from all accessible portals
                const pdfFiles = [];
                
                for (const portal of this.portals) {
                    try {
                        const files = await vaultService.query('getFilesByVaultId', {
                            vault_id: portal.id,
                            show_deleted: false
                        }, undefined, {vuexCache: false});
                        
                        if (files && Array.isArray(files)) {
                            const portalPdfs = files
                                .filter(file => file.extension === 'pdf' && file._deleted !== 1)
                                .map(file => ({
                                    id: file.id,
                                    name: file.name,
                                    extension: file.extension,
                                    portal_name: portal.name,
                                    portal_id: portal.id,
                                    vault_id: portal.id,
                                    org_id: portal.org_id
                                }));
                            pdfFiles.push(...portalPdfs);
                        }
                    } catch (error) {
                        console.error('Error loading files from portal:', portal.name, error);
                    }
                }
                
                this.existingPdfFiles = pdfFiles;
            } catch (error) {
                console.error('Error loading existing PDF files:', error);
                this.errorMessage = 'Failed to load existing PDF files';
            } finally {
                this.loadingFiles = false;
            }
        },
        async loadPdfViewer() {
            try {
                this.pdfViewerLoaded = false;
                
                // Get the PDF URL
                let pdfUrl = null;
                
                if (this.selectedFile) {
                    // Create a blob URL for the selected file
                    pdfUrl = URL.createObjectURL(this.selectedFile);
                } else if (this.selectedExistingFile) {
                    // Get signed URL for existing file
                    const fileInfo = this.existingPdfFiles.find(f => f.id === this.selectedExistingFile);
                    if (fileInfo) {
                        const vault = this.portals.find(p => p.id === fileInfo.vault_id);
                        if (vault) {
                            pdfUrl = await vaultService.getSignedFileUrl(fileInfo, vault, true);
                        }
                    }
                }
                
                if (!pdfUrl) {
                    throw new Error('Could not load PDF');
                }
                
                // Wait for DOM to be ready
                await this.$nextTick();
                await this.$nextTick(); // Double nextTick to ensure stepper content is rendered
                
                // Add a small delay to ensure DOM is fully rendered
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Verify the container element exists
                if (!this.$refs.pdfViewerContainer) {
                    console.error('PDF viewer container ref not found');
                    throw new Error('PDF viewer container not available. Please try again.');
                }
                
                // Initialize WebViewer with visible UI
                const instance = await WebViewer.Iframe({
                    path: '/webviewer/lib',
                    licenseKey: process.env.VUE_APP_pdftron_license_key,
                    initialDoc: pdfUrl,
                    extension: 'pdf'
                }, this.$refs.pdfViewerContainer);
                
                this.pdfViewerInstance = instance;
                
                // Automatically detect fields when document loads
                instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
                    // Wait a moment for form fields to be fully parsed
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    // Set up the viewer for field detection (read-only mode)
                    instance.UI.setLayoutMode(instance.UI.LayoutMode.Continuous);
                    instance.UI.setToolbarGroup('View');
                    
                    // Disable all editing and interaction tools
                    instance.UI.disableElements([
                        'toolbarGroup-Edit',
                        'toolbarGroup-Annotate',
                        'toolbarGroup-Forms',
                        'toolbarGroup-Insert',
                        'toolbarGroup-Measure',
                        'toolbarGroup-Shapes',
                        'toolbarGroup-FillAndSign'
                    ]);
                    
                    // Enable read-only mode to prevent field interaction
                    instance.Core.annotationManager.enableReadOnlyMode();
                    
                    // Make all form fields non-interactive
                    const fieldManager = instance.Core.annotationManager.getFieldManager();
                    const fields = fieldManager.getFields();
                    for (const fieldName in fields) {
                        const field = fields[fieldName];
                        if (field.widgets) {
                            field.widgets.forEach(widget => {
                                widget.ReadOnly = true;
                                widget.Hidden = false;
                                // For button fields (radio/checkbox), disable interaction
                                if (widget.field && widget.field.type === 'Btn') {
                                    widget.Hidden = false;
                                    widget.ReadOnly = true;
                                }
                            });
                        }
                    }
                    
                    // Also disable all widget annotations
                    const widgets = instance.Core.annotationManager.getAnnotationsList().filter(a => 
                        a instanceof instance.Core.Annotations.WidgetAnnotation
                    );
                    widgets.forEach(widget => {
                        widget.ReadOnly = true;
                        widget.Hidden = false;
                    });
                    
                    // Set up field interaction handlers
                    this.setupFieldInteractionHandlers(instance);
                    
                    // Detect fields
                    await this.detectExistingFields(instance);
                    
                    // Create visual overlays for fields
                    this.createFieldOverlays(instance);
                    
                    // Mark as loaded AFTER field detection is complete
                    this.pdfViewerLoaded = true;
                });
                
            } catch (error) {
                console.error('Error loading PDF viewer:', error);
                this.errorMessage = 'Failed to load PDF: ' + error.message;
                this.pdfViewerLoaded = true; // Show error state
            }
        },
        async detectExistingFields(instance) {
            try {
                const { Core } = instance;
                const annotationManager = Core.annotationManager;
                const fieldManager = annotationManager.getFieldManager();
                
                // Get all annotations (includes signature widgets)
                const annotations = annotationManager.getAnnotationsList();
                
                // Get all form fields
                const fields = fieldManager.getFields();
                
                const signatureFields = [];
                
                // Process form fields - ONLY signature fields
                for (const fieldName in fields) {
                    const field = fields[fieldName];
                    
                    // Get field type
                    const fieldType = field.type;
                    
                    // Only process signature fields
                    if (fieldType !== 'Sig') {
                        continue;
                    }
                    
                    // Use the field's name property if available, fallback to the key
                    const actualFieldName = field.name || fieldName;
                    
                    // Get all widgets for this field
                    const widgets = field.widgets || [];
                    
                    for (let i = 0; i < widgets.length; i++) {
                        const widget = widgets[i];
                        const rect = widget.getRect();
                        
                        // Create a better display name if the field name is just a number
                        let displayName = actualFieldName;
                        if (/^\d+$/.test(actualFieldName)) {
                            displayName = `Signature Field (Page ${widget.PageNumber})`;
                        }
                        
                        const fieldData = {
                            fieldName: actualFieldName,
                            displayName: displayName,
                            id: widget.Id,
                            type: 'Signature',
                            pageNumber: widget.PageNumber,
                            x: rect.x1,
                            y: rect.y1,
                            width: rect.x2 - rect.x1,
                            height: rect.y2 - rect.y1,
                            widget: widget,
                            field: field
                        };
                        
                        signatureFields.push(fieldData);
                    }
                }
                
                // Process signature annotations that might not be form fields
                for (const annot of annotations) {
                    if (annot.Subject === 'Signature' && annot.getContents && !annot.getContents()) {
                        // Empty signature annotation (signature placeholder)
                        const rect = annot.getRect();
                        
                        const fieldData = {
                            fieldName: 'Signature_' + annot.Id,
                            displayName: `Signature (Page ${annot.PageNumber})`,
                            id: annot.Id,
                            type: 'Signature',
                            pageNumber: annot.PageNumber,
                            x: rect.x1,
                            y: rect.y1,
                            width: rect.x2 - rect.x1,
                            height: rect.y2 - rect.y1,
                            annotation: annot
                        };
                        
                        // Check if we don't already have this
                        if (!signatureFields.find(f => f.id === annot.Id)) {
                            signatureFields.push(fieldData);
                        }
                    }
                }
                
                // Sort by page number, then Y position
                signatureFields.sort((a, b) => {
                    if (a.pageNumber !== b.pageNumber) {
                        return a.pageNumber - b.pageNumber;
                    }
                    return a.y - b.y;
                });
                
                this.signatureFields = signatureFields;
                
                console.log('Detected', signatureFields.length, 'signature fields');
                
                // Auto-assign signature fields to recipients (only in single mode)
                if (!this.bulkMode && this.recipients.length > 0) {
                    this.autoAssignFieldsToRecipients();
                }
                
            } catch (error) {
                console.error('Error detecting signature fields:', error);
                this.errorMessage = 'Error detecting PDF signature fields.';
                this.signatureFields = [];
            }
        },
        getFieldTypeName(fieldType) {
            const typeMap = {
                'Sig': 'Signature',
                'Tx': 'Text',
                'Btn': 'Button',
                'Ch': 'Choice'
            };
            return typeMap[fieldType] || fieldType || 'Field';
        },
        setupFieldInteractionHandlers(instance) {
            const { Core } = instance;
            const annotationManager = Core.annotationManager;
            
            // Listen for annotation clicks on signature fields
            annotationManager.addEventListener('annotationSelected', (annotations, action) => {
                if (action === 'selected' && annotations.length > 0) {
                    const annot = annotations[0];
                    // Find the signature field data for this annotation
                    const field = this.signatureFields.find(f => {
                        return f.widget && f.widget.Id === annot.Id;
                    });
                    
                    if (field) {
                        this.onFieldSelected(field);
                    }
                }
            });
        },
        createFieldOverlays(instance) {
            const { Core } = instance;
            const annotationManager = Core.annotationManager;
            const Annotations = Core.Annotations;
            
            // Create overlay rectangles and labels for each SIGNATURE field to make them more visible
            this.signatureFields.forEach(field => {
                const rect = field.widget ? field.widget.getRect() : {
                    x1: field.x,
                    y1: field.y,
                    x2: field.x + field.width,
                    y2: field.y + field.height
                };
                
                // Create a semi-transparent overlay rectangle
                const overlay = new Annotations.RectangleAnnotation({
                    PageNumber: field.pageNumber,
                    X: rect.x1,
                    Y: rect.y1,
                    Width: rect.x2 - rect.x1,
                    Height: rect.y2 - rect.y1,
                    StrokeThickness: 3, // Signature fields are prominent
                    FillColor: new Annotations.Color(200, 200, 255, 0.1),
                    StrokeColor: new Annotations.Color(100, 100, 200),
                    Author: 'System',
                    ReadOnly: true,
                    Listable: false,
                    NoMove: true,
                    NoResize: true
                });
                
                overlay.setCustomData('fieldId', field.id);
                overlay.setCustomData('isFieldOverlay', 'true');
                
                field.overlay = overlay;
                
                // Create a simple sticky note annotation as label above the field
                const labelY = rect.y1 - 20; // Position above the field
                
                try {
                    const label = new Annotations.StickyAnnotation({
                        PageNumber: field.pageNumber,
                        X: rect.x1,
                        Y: labelY,
                        Width: 20,
                        Height: 20,
                        StrokeThickness: 0,
                        Color: new Annotations.Color(255, 255, 0, 0.8),
                        Author: 'System',
                        ReadOnly: true,
                        Listable: false,
                        NoMove: true,
                        NoResize: true
                    });
                    
                    // Set the signature field name as content
                    const labelText = '✍ ' + (field.displayName || field.fieldName);
                    
                    label.setContents(labelText);
                    label.setCustomData('fieldId', field.id);
                    label.setCustomData('isFieldLabel', 'true');
                    
                    field.label = label;
                    
                    console.log('Created label for signature field:', field.fieldName, 'at position', rect.x1, labelY);
                } catch (error) {
                    console.error('Error creating label for signature field:', field.fieldName, error);
                }
            });
            
            // Update overlay colors based on assignments
            this.updateAllFieldOverlays();
        },
        updateAllFieldOverlays() {
            if (!this.pdfViewerInstance) return;
            
            const { Core } = this.pdfViewerInstance;
            const annotationManager = Core.annotationManager;
            const Annotations = Core.Annotations;
            
            // Remove old overlays and labels
            const existingOverlays = annotationManager.getAnnotationsList().filter(a => 
                a.getCustomData('isFieldOverlay') === 'true' || a.getCustomData('isFieldLabel') === 'true'
            );
            if (existingOverlays.length > 0) {
                annotationManager.deleteAnnotations(existingOverlays);
            }
            
            // Add updated overlays and labels for signature fields only
            this.signatureFields.forEach(field => {
                if (!field.overlay) return;
                
                // Update color based on assignment (recipient, organization, any guest, or not required)
                const assignment = this.fieldAssignments[field.id];
                let assignedColor = null;
                
                if (assignment === 'not_required') {
                    // Not required - grey color
                    assignedColor = this.hexToRgb('#9E9E9E');
                } else if (assignment === 'any_guest') {
                    // Any guest - cyan/teal color
                    assignedColor = this.hexToRgb('#00ACC1');
                } else if (assignment === 'org') {
                    // Assigned to organization
                    assignedColor = this.hexToRgb('#5764a9');
                } else if (assignment !== undefined && this.recipients[assignment]) {
                    // Assigned to specific recipient
                    const recipient = this.recipients[assignment];
                    assignedColor = this.hexToRgb(recipient.color);
                }
                
                if (assignedColor) {
                    field.overlay.StrokeColor = new Annotations.Color(assignedColor.r, assignedColor.g, assignedColor.b);
                    field.overlay.FillColor = new Annotations.Color(assignedColor.r, assignedColor.g, assignedColor.b, 0.2);
                } else {
                    // Default colors for unassigned signature fields
                    field.overlay.StrokeColor = new Annotations.Color(100, 100, 200);
                    field.overlay.FillColor = new Annotations.Color(200, 200, 255, 0.1);
                }
                
                annotationManager.addAnnotation(field.overlay);
                
                // Update and add label
                if (field.label) {
                    const labelText = '✍ ' + (field.displayName || field.fieldName);
                    field.label.setContents(labelText);
                    
                    // Update label color to match assignment
                    if (assignedColor) {
                        field.label.Color = new Annotations.Color(assignedColor.r, assignedColor.g, assignedColor.b, 0.8);
                    } else {
                        field.label.Color = new Annotations.Color(255, 255, 0, 0.8);
                    }
                    
                    annotationManager.addAnnotation(field.label);
                }
            });
            
            const allAnnotations = this.signatureFields.map(f => [f.overlay, f.label]).flat().filter(Boolean);
            annotationManager.drawAnnotationsFromList(allAnnotations);
        },
        onFieldSelected(field) {
            // Emit event or show UI to configure this field
            console.log('Field selected:', field);
            // Store selected field ID for UI to show configuration panel
            this.selectedFieldId = field.id;
        },
        autoAssignFieldsToRecipients() {
            if (this.recipients.length === 0 || this.signatureFields.length === 0) {
                return;
            }
            
            // Sort signature fields by page number, then Y position (top to bottom)
            const sortedFields = [...this.signatureFields].sort((a, b) => {
                if (a.pageNumber !== b.pageNumber) {
                    return a.pageNumber - b.pageNumber;
                }
                return a.y - b.y;
            });
            
            // Assign fields sequentially to recipients (cycle if more fields than recipients)
            const assignments = {};
            sortedFields.forEach((field, index) => {
                const recipientIndex = index % this.recipients.length;
                assignments[field.id] = recipientIndex;
            });
            
            this.fieldAssignments = assignments;
            
            // Update visual overlays
            if (this.pdfViewerInstance) {
                this.updateAllFieldOverlays();
            }
            
            console.log('Auto-assigned', sortedFields.length, 'signature fields to', this.recipients.length, 'recipients');
        },
        assignFieldToRecipient(fieldId, recipientIndex) {
            // Use Vue.set for better reactivity in Vue 2
            this.$set(this.fieldAssignments, fieldId, recipientIndex);
            
            // Update visual overlays
            if (this.pdfViewerInstance) {
                this.updateAllFieldOverlays();
            }
        },
        hexToRgb(hex) {
            // Remove # if present
            hex = hex.replace('#', '');
            
            // Parse hex to RGB
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            
            return { r, g, b };
        },
        async sendSignatureRequest() {
            this.sending = true;
            this.sendingProgress = 0;
            this.currentSendingPortal = '';
            this.errorMessage = null;
            this.successMessage = null;
            
            try {
                // Validate we have recipients (only in single mode)
                if (!this.bulkMode) {
                    if (!this.recipients || this.recipients.length === 0) {
                        throw new Error('Please add at least one recipient');
                    }
                }
                
                // In bulk mode, recipients are not used - fields are assigned to "Any Guest", "Organization", or "Not Required"
                
                // Check if the selected file already has an active signature request
                let fileId = this.selectedExistingFile || (this.selectedFile && this.selectedFile.id);
                if (fileId && this.signatureRequestsByFile && this.signatureRequestsByFile[fileId]) {
                    const fileRequests = this.signatureRequestsByFile[fileId];
                    const activeRequest = fileRequests.find(req => 
                        req.status !== 'completed' && 
                        req.status !== 'cancelled' && 
                        req._deleted !== 1
                    );
                    
                    if (activeRequest) {
                        throw new Error('This file already has an active signature request. Please wait for it to be completed or cancelled before sending a new request.');
                    }
                }
                
                // Set participant custom data on annotations BEFORE exporting XFDF
                console.log('Setting participant assignments on annotations before export...');
                const annotationManager = this.pdfViewerInstance.Core.annotationManager;
                const allAnnotations = annotationManager.getAnnotationsList();
                
                this.signatureFields.forEach(field => {
                    // Find the annotation for this field
                    const annot = allAnnotations.find(a => a.Id === field.id || a.fieldName === field.fieldName);
                    if (!annot) {
                        console.warn('Could not find annotation for field:', field.fieldName);
                        return;
                    }
                    
                    // Get the assignment (can be recipient index, 'org', or 'not_required')
                    const assignment = this.fieldAssignments[field.id];
                    
                    if (assignment === 'not_required') {
                        // Field is optional/not required
                        annot.setCustomData('isNotRequired', 'true');
                        annot.setCustomData('participantEmail', '');
                        annot.setCustomData('participantId', '');
                        annot.setCustomData('participantName', 'Not Required');
                        annot.setCustomData('participantColor', '#9E9E9E');
                        
                        console.log(`Field "${field.fieldName}" marked as NOT REQUIRED (optional)`);
                    } else if (assignment === 'any_guest') {
                        // Field can be signed by any guest
                        annot.setCustomData('isAnyGuest', 'true');
                        annot.setCustomData('participantEmail', 'any_guest');
                        annot.setCustomData('participantId', 'any_guest');
                        annot.setCustomData('participantName', 'Any Guest');
                        annot.setCustomData('participantColor', '#00ACC1');
                        
                        console.log(`Field "${field.fieldName}" marked as ANY GUEST (any portal guest can sign)`);
                    } else if (assignment === 'org') {
                        // Assigned to organization
                        const orgEmail = `org:${this.organization.id}`;
                        annot.setCustomData('participantEmail', orgEmail);
                        annot.setCustomData('participantId', orgEmail);
                        annot.setCustomData('participantName', this.organization.companyName);
                        annot.setCustomData('participantColor', '#5764a9');
                        annot.setCustomData('isOrganization', 'true');
                        
                        console.log(`Assigned field "${field.fieldName}" to organization: ${this.organization.companyName}`);
                    } else if (assignment !== undefined && this.recipients[assignment]) {
                        // Assigned to specific recipient
                        const recipient = this.recipients[assignment];
                        
                        // Set custom data that will be preserved in XFDF
                        annot.setCustomData('participantEmail', recipient.email);
                        annot.setCustomData('participantId', recipient.email);
                        annot.setCustomData('participantName', recipient.name || recipient.email);
                        annot.setCustomData('participantColor', recipient.color);
                        annot.setCustomData('recipientIndex', assignment.toString());
                        
                        console.log(`Assigned field "${field.fieldName}" to ${recipient.email}`);
                    } else {
                        console.warn(`Field "${field.fieldName}" has no assignment`);
                    }
                });
                
                // Get XFDF data for signature fields (now includes participant assignments)
                // Filter out temporary annotations:
                // - Field label annotations (StickyAnnotations showing field names)
                // - Standalone FreeText comment annotations without participant assignments
                const allAnnotationsForExport = annotationManager.getAnnotationsList();
                
                console.log('📊 Before filtering:', {
                    totalAnnotations: allAnnotationsForExport.length,
                    bySubject: allAnnotationsForExport.reduce((acc, a) => {
                        acc[a.Subject] = (acc[a.Subject] || 0) + 1;
                        return acc;
                    }, {})
                });
                
                const functionalAnnotations = allAnnotationsForExport.filter(annot => {
                    // Filter out field labels (StickyAnnotations with isFieldLabel custom data)
                    // These are temporary helpers showing signature field names and should NOT be saved
                    const isFieldLabel = annot.getCustomData('isFieldLabel') === 'true';
                    if (isFieldLabel) {
                        console.log(`Filtering out field label annotation: ${annot.getContents ? annot.getContents() : 'no content'}`);
                        return false;
                    }
                    
                    // Keep all non-FreeText annotations (except field labels, handled above)
                    const isFreeText = annot.Subject === 'Free Text' || 
                                      (annot instanceof this.pdfViewerInstance.Core.Annotations.FreeTextAnnotation);
                    
                    if (!isFreeText) {
                        return true; // Keep all non-FreeText (widgets, signatures, etc.)
                    }
                    
                    // For FreeText: ONLY keep if it has a participant assignment
                    // Field name comments (showing signature field names) should be filtered out
                    // These are temporary helpers for mapping participants and should NOT be saved
                    const hasParticipant = annot.getCustomData('participantEmail') || annot.getCustomData('participantId');
                    
                    console.log(`FreeText annotation: fieldName="${annot.fieldName}", hasParticipant=${hasParticipant}, keeping=${hasParticipant}`);
                    
                    // Only keep FreeText if it has a participant assignment (it's functional)
                    // Filter out FreeText that are just field name labels/comments
                    return hasParticipant;
                });
                
                const filteredCount = allAnnotationsForExport.length - functionalAnnotations.length;
                console.log(`✅ Exporting ${functionalAnnotations.length} annotations (filtered out ${filteredCount} temporary annotations: field labels and standalone comments)`);
                
                // Export the functional annotations WITH custom data (participant assignments)
                const xfdfString = await annotationManager.exportAnnotations({ 
                    annotList: functionalAnnotations,
                    useDisplayAuthor: false,
                    widgets: true,
                    links: true,
                    fields: true
                });
                console.log('XFDF exported with participant assignments');
                console.log('📝 Verifying custom data in XFDF...');
                
                // Verify that custom data was included
                if (xfdfString.includes('participantEmail')) {
                    console.log('✅ Custom data (participantEmail) found in XFDF');
                } else {
                    console.warn('⚠️ Custom data (participantEmail) NOT found in XFDF - assignments may not persist!');
                }
                
                // Extract field assignments as clean JSON (separate from XFDF)
                // This prevents XFDF import from overwriting field values during signing
                console.log('📋 Extracting field assignments as JSON...');
                const fieldAssignments = {};
                
                allAnnotationsForExport.forEach(annot => {
                    // Only process form fields (widgets)
                    if (annot.Subject === 'Widget' && annot.fieldName) {
                        const participantEmail = annot.getCustomData && annot.getCustomData('participantEmail');
                        const participantId = annot.getCustomData && annot.getCustomData('participantId');
                        const participantName = annot.getCustomData && annot.getCustomData('participantName');
                        const participantColor = annot.getCustomData && annot.getCustomData('participantColor');
                        const isOrganization = annot.getCustomData && annot.getCustomData('isOrganization');
                        const isAnyGuest = annot.getCustomData && annot.getCustomData('isAnyGuest');
                        const isRequired = annot.getCustomData && annot.getCustomData('isRequired');
                        
                        // Only include fields that have participant assignments
                        if (participantEmail || participantId) {
                            fieldAssignments[annot.fieldName] = {
                                participantEmail: participantEmail || null,
                                participantId: participantId || null,
                                participantName: participantName || null,
                                participantColor: participantColor || '#1976D2',
                                isOrganization: isOrganization === 'true',
                                isAnyGuest: isAnyGuest === 'true',
                                isRequired: isRequired === 'true',
                                fieldType: annot.fieldName.toLowerCase().includes('signature') ? 'signature' : 'field',
                                subject: annot.Subject
                            };
                        }
                    }
                });
                
                console.log('✅ Extracted', Object.keys(fieldAssignments).length, 'field assignments');
                console.log('📋 Field assignments:', fieldAssignments);
                
                // Determine portal ID(s) based on mode
                let portalId = this.selectedPortal;
                let portalName = null;
                let portalsToProcess = [];
                
                if (this.bulkMode) {
                    // In bulk mode, process all selected portals
                    portalsToProcess = this.selectedPortalsData;
                    console.log(`Bulk mode: sending to ${portalsToProcess.length} portals`);
                } else {
                    // Single portal mode
                    if (this.createNewPortal) {
                        portalName = this.newPortalName;
                        portalId = null;
                    }
                }
                
                // Prepare file data
                let fileData = null;
                let fileName = null;
                
                if (this.selectedFile) {
                    // Convert file to base64
                    fileData = await this.fileToBase64(this.selectedFile);
                    fileName = this.selectedFile.name;
                    console.log('Uploading new file:', fileName);
                } else if (this.selectedExistingFile) {
                    const fileInfo = this.existingPdfFiles.find(f => f.id === this.selectedExistingFile);
                    if (fileInfo) {
                        fileId = fileInfo.id;
                        fileName = fileInfo.name;
                        console.log('Using existing file:', {
                            fileId: fileId,
                            fileName: fileName,
                            fileVaultId: fileInfo.vault_id,
                            selectedPortalId: portalId
                        });
                        
                        // Validate that the file belongs to the selected portal
                        if (fileInfo.vault_id !== portalId) {
                            throw new Error(`File "${fileName}" does not belong to the selected portal. Please select a file from the same portal or upload a new file.`);
                        }
                    } else {
                        throw new Error('Selected file not found in loaded files list');
                    }
                } else {
                    throw new Error('Please select or upload a PDF file');
                }
                
                // Send signature request to each recipient
                // Build recipients array with assigned fields
                // In bulk mode, this will be empty initially and only contain org/any_guest recipients
                const recipientsArray = this.bulkMode ? [] : this.recipients.map((recipient, index) => {
                    // Get fields assigned to this recipient
                    // CRITICAL: We must send field NAMES, not IDs, because IDs can change between sessions
                    // while fieldNames are stable in the PDF form.
                    const assignedFieldNames = Object.keys(this.fieldAssignments)
                        .filter(fieldId => this.fieldAssignments[fieldId] === index)
                        .map(fieldId => {
                            const field = this.signatureFields.find(f => f.id === fieldId);
                            return field ? field.fieldName : null;
                        })
                        .filter(name => name !== null);
                    
                    return {
                        email: recipient.email,
                        name: recipient.name || null,
                        order: index,
                        assigned_fields: assignedFieldNames,
                        color: recipient.color || null
                    };
                });
                
                console.log(`Starting with ${recipientsArray.length} individual recipients (bulk mode: ${this.bulkMode})`);
                
                // CRITICAL: Add organization recipients from signature field assignments
                // Organizations are assigned to fields but not in the recipients list
                // Reuse annotationManager and allAnnotations from above (already declared)
                
                // Find all signature fields assigned to organizations
                const orgRecipients = [];
                allAnnotationsForExport.forEach(annot => {
                    if (annot.Subject === 'Widget' && annot.fieldName) {
                        const participantEmail = annot.getCustomData && annot.getCustomData('participantEmail');
                        const participantName = annot.getCustomData && annot.getCustomData('participantName');
                        const participantColor = annot.getCustomData && annot.getCustomData('participantColor');
                        const isOrganization = annot.getCustomData && annot.getCustomData('isOrganization');
                        
                        // Check if this is an organization assignment
                        if (participantEmail && participantEmail.startsWith('org:') && isOrganization === 'true') {
                            // Check if we already added this org
                            const alreadyAdded = orgRecipients.some(r => r.email === participantEmail) ||
                                                recipientsArray.some(r => r.email === participantEmail);
                            
                            if (!alreadyAdded) {
                                orgRecipients.push({
                                    email: participantEmail,
                                    name: participantName || null,
                                    order: recipientsArray.length + orgRecipients.length,
                                    assigned_fields: [annot.fieldName], // Use fieldName not Id
                                    color: participantColor || null
                                });
                            } else {
                                // Add this field to existing org recipient
                                const existing = orgRecipients.find(r => r.email === participantEmail) || 
                                                recipientsArray.find(r => r.email === participantEmail);
                                if (existing && !existing.assigned_fields.includes(annot.fieldName)) {
                                    existing.assigned_fields.push(annot.fieldName);
                                }
                            }
                        }
                    }
                });
                
                // Merge organization recipients into the main array
                recipientsArray.push(...orgRecipients);
                
                // CRITICAL: Add "Any Guest" recipients from signature field assignments
                // The backend needs this in the recipients array to:
                // 1. Display in signature tracking UI
                // 2. Know which guests in the portal can access these fields
                // Backend should NOT send invitation emails to "any_guest" - it's a special marker
                const anyGuestRecipients = [];
                allAnnotationsForExport.forEach(annot => {
                    if (annot.Subject === 'Widget' && annot.fieldName) {
                        const participantEmail = annot.getCustomData && annot.getCustomData('participantEmail');
                        const participantName = annot.getCustomData && annot.getCustomData('participantName');
                        const participantColor = annot.getCustomData && annot.getCustomData('participantColor');
                        const isAnyGuest = annot.getCustomData && annot.getCustomData('isAnyGuest');
                        
                        // Check if this is an "any guest" assignment
                        if (participantEmail === 'any_guest' && isAnyGuest === 'true') {
                            // Check if we already added "any_guest"
                            const alreadyAdded = anyGuestRecipients.some(r => r.email === 'any_guest') ||
                                                recipientsArray.some(r => r.email === 'any_guest');
                            
                            if (!alreadyAdded) {
                                anyGuestRecipients.push({
                                    email: 'any_guest',
                                    name: 'Any Guest',
                                    order: recipientsArray.length + anyGuestRecipients.length,
                                    assigned_fields: [annot.fieldName],
                                    color: participantColor || '#00ACC1',
                                    is_any_guest: true // Flag for backend to know this is special
                                });
                            } else {
                                // Add this field to existing any_guest recipient
                                const existing = anyGuestRecipients.find(r => r.email === 'any_guest') || 
                                                recipientsArray.find(r => r.email === 'any_guest');
                                if (existing && !existing.assigned_fields.includes(annot.fieldName)) {
                                    existing.assigned_fields.push(annot.fieldName);
                                }
                            }
                        }
                    }
                });
                
                // Merge "any guest" recipients into the main array
                recipientsArray.push(...anyGuestRecipients);
                
                console.log(`📧 Total recipients (including orgs and any guest): ${recipientsArray.length}`, recipientsArray.map(r => r.email));
                if (anyGuestRecipients.length > 0) {
                    console.log('👥 "Any Guest" recipients added - backend will NOT send invitation emails');
                }
                console.log('📋 Using field_assignments instead of XFDF for cleaner storage');
                console.log('📊 Field assignments being sent:', Object.keys(fieldAssignments).length, 'fields');
                console.log('📦 Field assignments will be stringified for AWSJSON type:', JSON.stringify(fieldAssignments).length, 'chars');
                
                // Send signature request(s) - either one for single mode or multiple for bulk mode
                const responses = [];
                
                if (this.bulkMode && portalsToProcess.length > 0) {
                    // Bulk mode: send separate request for each portal
                    // Each portal gets a new copy of the PDF with a unique file ID
                    this.sendingProgress = 0;
                    const successfulPortals = [];
                    const failedPortals = [];
                    
                    for (let i = 0; i < portalsToProcess.length; i++) {
                        const portal = portalsToProcess[i];
                        
                        try {
                            // Update progress
                            this.sendingProgress = Math.round((i / portalsToProcess.length) * 100);
                            this.currentSendingPortal = portal.name;
                            
                            const mutationParams = {
                                recipients: recipientsArray,
                                portal_id: portal.id,
                                portal_name: portal.name,
                                file_id: null, // Let backend create new file ID for each portal
                                file_data: fileData,
                                file_name: fileName,
                                // XFDF is optional now - field_assignments is cleaner and smaller
                                // required_fields_xfdf: xfdfString,
                                field_assignments: JSON.stringify(fieldAssignments), // AWSJSON requires string
                                custom_message: this.customMessage
                            };
                            
                            console.log(`Sending signature request to portal ${portal.name} (${i + 1}/${portalsToProcess.length})`, {
                                recipients: recipientsArray.map(r => r.email),
                                portalId: portal.id
                            });
                            
                            const response = await vaultService.mutation('sendSignatureRequest', mutationParams);
                            responses.push(response);
                            successfulPortals.push(portal.name);
                            console.log(`✅ Sent to portal ${portal.name}`);
                        } catch (error) {
                            console.error(`❌ Error sending signature request to portal ${portal.name}:`, error);
                            failedPortals.push({ name: portal.name, error: error.message || 'Unknown error' });
                            // Continue with other portals even if one fails
                        }
                    }
                    
                    // Complete progress
                    this.sendingProgress = 100;
                    
                    // Show appropriate message based on results
                    if (failedPortals.length === 0) {
                        // All succeeded
                        this.successMessage = `✅ Signature requests sent successfully to all ${successfulPortals.length} portal${successfulPortals.length !== 1 ? 's' : ''}!`;
                    } else if (successfulPortals.length === 0) {
                        // All failed
                        this.errorMessage = `❌ Failed to send signature requests to all portals:\n${failedPortals.map(p => `• ${p.name}: ${p.error}`).join('\n')}`;
                    } else {
                        // Partial success
                        this.successMessage = `⚠️ Sent to ${successfulPortals.length} portal${successfulPortals.length !== 1 ? 's' : ''}, but ${failedPortals.length} failed:\n${failedPortals.map(p => `• ${p.name}: ${p.error}`).join('\n')}`;
                    }
                } else {
                    // Single portal mode
                    const mutationParams = {
                        recipients: recipientsArray,
                        portal_id: portalId,
                        portal_name: portalName,
                        file_id: fileId,
                        file_data: fileData,
                        file_name: fileName,
                        // XFDF is optional now - field_assignments is cleaner and smaller
                        // required_fields_xfdf: xfdfString,
                        field_assignments: JSON.stringify(fieldAssignments), // AWSJSON requires string
                        custom_message: this.customMessage
                    };
                    
                    console.log(`Sending signature request to ${recipientsArray.length} recipient(s)`, {
                        recipients: recipientsArray.map(r => r.email),
                        hasFileId: !!fileId,
                        hasFileData: !!fileData,
                        portalId: portalId,
                        portalName: portalName,
                        fileName: fileName
                    });
                    
                    const response = await vaultService.mutation('sendSignatureRequest', mutationParams);
                    responses.push(response);
                    
                    this.successMessage = `Signature request sent successfully to ${recipientsArray.length} recipient(s)!`;
                }
                
                // Close dialog after a short delay
                setTimeout(() => {
                    this.$emit('close');
                    this.$emit('sent', responses);
                }, 2000);
                
            } catch (error) {
                console.error('Error sending signature request:', error);
                this.errorMessage = 'Failed to send signature request: ' + (error.message || 'Unknown error');
            } finally {
                this.sending = false;
                this.sendingProgress = 0;
                this.currentSendingPortal = '';
            }
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    // Remove data URL prefix
                    const base64 = reader.result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = error => reject(error);
            });
        },
        editPdfForms() {
            // Emit event to parent to open the PDF in the portal viewer with the Forms tab
            const fileInfo = this.selectedExistingFile ? 
                this.existingPdfFiles.find(f => f.id === this.selectedExistingFile) : 
                this.selectedFile;
            
            if (fileInfo) {
                this.$emit('edit-pdf', fileInfo);
            } else {
                this.errorMessage = 'Please select a PDF file first';
            }
        }
    },
    beforeDestroy() {
        // Clean up WebViewer instance
        if (this.pdfViewerInstance) {
            try {
                this.pdfViewerInstance.UI.closeDocument();
            } catch (error) {
                console.error('Error closing PDF viewer:', error);
            }
        }
    }
});





